import { Component, OnInit, ViewChild } from '@angular/core';
import { IonModal, ModalController } from '@ionic/angular';
import MainScene from '../Scenes/MainScene';
import { Place, PlacesList } from 'src/classes/dataClasses/Places';
import { FueFolletList } from 'src/classes/dataClasses/FueFolletMappings';
import { LatLonToXYConverter } from 'src/classes/LatLonToXY';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environment';

@Component({
  selector: 'app-mini-map',
  templateUrl: './mini-map.component.html',
  styleUrls: ['./mini-map.component.scss'],
})
export class MiniMapComponent  implements OnInit {
  @ViewChild(IonModal) modal: IonModal;
  @ViewChild('miniMapCanvas', { static: true }) miniMapCanvas;

  imgWidth = 720;// px
  imgHeight = 600;// px
  scale = 1;

  mainSceneWidth = 115200; // px
  mainSceneHeight = 96000; // px

  blinkVisible = true;
  blinkInterval: any;
  selectedPlaces: Place[] = [];
  activePlayers: any[] = [];

  constructor(private modalCtrl: ModalController, private http: HttpClient) {
    const selectedLocationNames = FueFolletList.filter(f => !f.hideOnMap).map(f => f.startingPlace);
    this.selectedPlaces = PlacesList.filter(p => selectedLocationNames.includes(p.name) && p.name !== "Monroe Park" );
  }


  getOtherPlayers(){
    // Fetch active players from API
    this.http.get(`${environment.ACTIVE_PLAYERS_API}/active-players`).subscribe((players: any[]) => {
      this.activePlayers = players;
      console.log("fetched active players:", this.activePlayers);
    });
  }

  ngOnInit() {
    const canvas: HTMLCanvasElement = this.miniMapCanvas?.nativeElement;
    if (canvas) {
      canvas.width = this.imgWidth;
      canvas.height = this.imgHeight;
      canvas.style.zIndex = '10';
    }
    this.getOtherPlayers();
    this.blinkInterval = setInterval(() => {
      // Clear canvas once per frame
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.blinkVisible = !this.blinkVisible;
      // Draw landmarks as static blue dots
      this.drawLandmarkDots();
      // Draw waypoint as green dot
      this.drawWaypoint();
      // Draw active players as orange dots
      this.drawActivePlayers();
      // Draw the player as a red flashing dot
      this.drawPlayerDot();
      // Draw legend
      this.drawLegend();
    }, 500);

    // Update other players positions every 10 seconds
    setInterval(() => {
      this.getOtherPlayers();
    }, 10000); // every 10 seconds

    // Initial draw
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.drawLandmarkDots();
    this.drawActivePlayers();
    this.drawPlayerDot();
    this.drawLegend();
  }

  ngOnDestroy() {
    clearInterval(this.blinkInterval);
  }

  closeModal() {
    this.modalCtrl.dismiss();
  }

  drawDot(x: number, y: number, color: string, flash: boolean = false) {
    const canvas: HTMLCanvasElement = this.miniMapCanvas?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const xRatio = x / this.mainSceneWidth;
    const yRatio = y / this.mainSceneHeight;
    const dotX = xRatio * this.imgWidth;
    const dotY = yRatio * this.imgHeight;
    if (!flash || (flash && this.blinkVisible)) {
      ctx.beginPath();
      ctx.arc(dotX, dotY, 8, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.closePath();
    }
  }

  drawPlayerDot() {
    if (MainScene.playerX === undefined || MainScene.playerY === undefined) return;
    // Project player position to mini map 
    this.drawDot(MainScene.playerX, MainScene.playerY, 'red', true);
  }

  drawLandmarkDots() {
    this.selectedPlaces.forEach(place => {
      const { x, y } = LatLonToXYConverter.latlngToScreenXY(place.coords.latitude, place.coords.longitude);
      this.drawDot(x, y, 'blue');
      this.addLabel(place.name, x, y);
    });
  }

  drawWaypoint() {

    // 1. First filter out FueFollets that should be hidden on the map.
    const visibleFueFollets = FueFolletList.filter(fueFollet => !fueFollet.hideOnMap);

    // 2. Further filter to include only those whose prerequisites are met.
    const viableFueFollets = visibleFueFollets.filter(fueFollet => {
      if (!fueFollet.tokenPrerequisite) {
        return true;
      }
      return MainScene.tokens.includes(fueFollet.tokenPrerequisite);
    });

    // 3. Find the index of last viable FueFollet in the visibleFueFollets list.
    const lastViableFueFollet = viableFueFollets[viableFueFollets.length - 1];

    let waypoint: Place | undefined;

    const nextVisibleFueFolletIndex = visibleFueFollets.indexOf(lastViableFueFollet) + 1;
    if (nextVisibleFueFolletIndex < visibleFueFollets.length) {
      const nextVisibleFueFollet = visibleFueFollets[nextVisibleFueFolletIndex];
      waypoint = this.selectedPlaces.find(place =>
        place.name === nextVisibleFueFollet.startingPlace
      );
    }
    if(nextVisibleFueFolletIndex === visibleFueFollets.length){
      waypoint = this.selectedPlaces.find(place =>
        place.name === lastViableFueFollet.endingPlace
      );
    }

    if (waypoint) {
      const { x, y } = LatLonToXYConverter.latlngToScreenXY(waypoint.coords.latitude, waypoint.coords.longitude);
      this.drawDot(x, y, 'green');
      this.addLabel(waypoint.name, x, y);
    }
  }

  addLabel(name: string, x: number, y: number) {
    const canvas: HTMLCanvasElement = this.miniMapCanvas?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const xRatio = x / this.mainSceneWidth;
    const yRatio = y / this.mainSceneHeight;
    const labelX = xRatio * this.imgWidth;
    const labelY = yRatio * this.imgHeight + 16; // 16px below the dot
    ctx.font = '12px Arial';
    ctx.fillStyle = 'black';
    ctx.textAlign = 'center';
    ctx.fillText(name, labelX, labelY);
  }

  drawLegend() {
    const canvas: HTMLCanvasElement = this.miniMapCanvas?.nativeElement;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const legendX = 20;
    // Move legend up to fit all items
    const legendY = this.imgHeight - 90;
    const legendWidth = 110;
    const legendHeight = 100;
    // Draw white rectangle background
    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = 'white';
    ctx.fillRect(legendX - 10, legendY - 20, legendWidth, legendHeight);
    ctx.restore();
    // Red dot: You
    ctx.beginPath();
    ctx.arc(legendX, legendY, 8, 0, 2 * Math.PI);
    ctx.fillStyle = 'red';
    ctx.fill();
    ctx.closePath();
    ctx.font = '12px Arial';
    ctx.fillStyle = 'black';
    ctx.textAlign = 'left';
    ctx.fillText('You', legendX + 18, legendY + 4);
    // Blue dot: Landmark
    ctx.beginPath();
    ctx.arc(legendX, legendY + 20, 8, 0, 2 * Math.PI);
    ctx.fillStyle = 'blue';
    ctx.fill();
    ctx.closePath();
    ctx.fillStyle = 'black';
    ctx.fillText('Landmarks', legendX + 18, legendY + 24);
    // Green dot: Go Here (Waypoint)
    ctx.beginPath();
    ctx.arc(legendX, legendY + 40, 8, 0, 2 * Math.PI);
    ctx.fillStyle = 'green';
    ctx.fill();
    ctx.closePath();
    ctx.fillStyle = 'black';
    ctx.fillText('Go Here', legendX + 18, legendY + 44);
    // Orange dot: other players
    ctx.beginPath();
    ctx.arc(legendX, legendY + 60, 8, 0, 2 * Math.PI);
    ctx.fillStyle = 'orange';
    ctx.fill();
    ctx.closePath();
    ctx.fillStyle = 'black';
    ctx.fillText('Other Players', legendX + 18, legendY + 64);
  }

  drawActivePlayers() {
    this.activePlayers.forEach(player => {
      const x = parseFloat(player.playerPosition.M.x.N);
      const y = parseFloat(player.playerPosition.M.y.N);
      this.drawDot(x, y, 'orange');
    });
  }


}
